import React, { useCallback, useState } from 'react'

interface DropTarget {
  id: string
  position: 'before' | 'after'
}

interface DragReorderResult<T extends { id: string }> {
  dropTarget: DropTarget | null
  handleDragStart: (e: React.DragEvent, itemId: string) => void
  handleDragOver: (e: React.DragEvent, itemId: string) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent, targetId: string) => void
  handleDragEnd: () => void
  reorder: (items: T[]) => T[] | null
}

/**
 * Manages drag-and-drop reordering for a list of items.
 */
export function useDragReorder<T extends { id: string }>(
  dataTransferKey: string
): DragReorderResult<T> {
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)

  const handleDragStart = useCallback(
    (e: React.DragEvent, itemId: string) => {
      e.dataTransfer.setData(dataTransferKey, itemId)
      e.dataTransfer.effectAllowed = 'move'
      setDraggedId(itemId)
    },
    [dataTransferKey]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent, itemId: string) => {
      e.preventDefault()
      if (!e.dataTransfer.types.includes(dataTransferKey)) return

      const rect = e.currentTarget.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const position: 'before' | 'after' =
        mouseX < rect.width / 2 ? 'before' : 'after'

      setDropTarget({ id: itemId, position })
    },
    [dataTransferKey]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDropTarget(null)
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent, targetId: string) => {
      e.preventDefault()
      const id = e.dataTransfer.getData(dataTransferKey)
      if (id) setDraggedId(id)
      setDropTarget((prev) => (prev?.id === targetId ? prev : null))
    },
    [dataTransferKey]
  )

  const handleDragEnd = useCallback(() => {
    setDropTarget(null)
    setDraggedId(null)
  }, [])

  const reorder = useCallback(
    (items: T[]): T[] | null => {
      if (!draggedId || !dropTarget || draggedId === dropTarget.id) return null

      const list = [...items]
      const dragIdx = list.findIndex((w) => w.id === draggedId)
      const targetIdx = list.findIndex((w) => w.id === dropTarget.id)
      if (dragIdx === -1 || targetIdx === -1) return null

      const [dragged] = list.splice(dragIdx, 1)
      const newTargetIdx = list.findIndex((w) => w.id === dropTarget.id)
      const insertIdx =
        dropTarget.position === 'before' ? newTargetIdx : newTargetIdx + 1
      list.splice(insertIdx, 0, dragged)

      setDropTarget(null)
      setDraggedId(null)
      return list
    },
    [draggedId, dropTarget]
  )

  return {
    dropTarget,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    reorder,
  }
}
